import { AuthAPI } from "@api/Auth.api";
import ButtonVariants from "@core-components/button/ButtonVariants";
import inputsClassnames from "@core-components/inputs/InputClassnames";
import SSHeadline from "@core-components/title/SSHeadline";
import SSWhiteText from "@core-components/title/components/SSWhiteText";
import { onEnter } from "@helpers/utils";
import useAppTranslation from "@hooks/language/useAppTranslation";
import { Divider, Text, TextInput, ThemeIcon } from "@mantine/core";
import { useForm } from "@mantine/form";
import useLoginModalStore, { ELoginModalState } from "@store/loginModal.store";
import { IconArrowRight, IconMailFilled } from "@tabler/icons-react";
import { useState } from "react";
import OAuthButtons from "./OAuthButtons";

const RegisterModalBody: React.FC = () => {
  const { t } = useAppTranslation("components.modals.auth.register");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const form = useForm({
    initialValues: {
      email: "",
    },
    validate: {
      email: (value: string) =>
        /^\S+@\S+$/.test(value) ? null : t("validation.email"),
    },
  });
  const { open } = useLoginModalStore();

  const handleSubmit = () => {
    form.validate();
    if (form.isValid()) {
      setIsLoading(true);
      AuthAPI.register(form.values.email)
        .then(() => open(ELoginModalState.OTP, form.values.email))
        .finally(() => setIsLoading(false));
    }
  };

  return (
    <>
      <SSHeadline
        props={{
          title: { classNames: { root: "w-max" }, size: 35 },
          stack: { align: "start", className: "font-inter" },
        }}
        subTitle={
          <span className="mt-[10px] text-[14px] text-primary-dark-text">
            <span>{t("hasAccount")}</span>
            <Text
              span
              inherit
              className="cursor-pointer text-secret-purple-default hover:underline"
              onClick={() => {
                open(ELoginModalState.LOGIN);
              }}
            >
              {t("loginLink")}
            </Text>
          </span>
        }
      >
        <SSWhiteText classNames={{ root: "font-secret-narrow" }}>
          {t("title")}
        </SSWhiteText>
      </SSHeadline>
      <OAuthButtons />
      <Divider
        label={<Text className="sm:px-9">{t("divider")}</Text>}
        className="font-inter"
      />
      <div className="space-y-3">
        <TextInput
          autoFocus
          placeholder={t("emailPlaceholder")}
          leftSection={<IconMailFilled size={16} />}
          classNames={inputsClassnames}
          onKeyDown={(e) => onEnter(e, handleSubmit)}
          {...form.getInputProps("email")}
        />
        <ButtonVariants.Gradient
          isLoading={isLoading}
          isFullWidth
          onClick={handleSubmit}
          className="gradient-blue-light-purple rounded-lg"
          label={
            <>
              {t("submitButton")}{" "}
              <ThemeIcon className="ml-2" color="white" variant="transparent">
                <IconArrowRight />
              </ThemeIcon>
            </>
          }
        />
      </div>
    </>
  );
};

export default RegisterModalBody;
